'use client';

import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import {styled} from '@mui/material/styles';
import type {ImageProps} from 'next/image';
import type {FC, ReactNode} from 'react';
import {Section, Typography} from '../primitives';
import {ListingCard, type ListingCardProps} from './ListingCard';
import type {ListingEmptyStateTone} from './ListingEmptyState';
import {ListingFilterBar, type ListingFilterItem} from './ListingFilterBar';
import {ListingGrid} from './ListingGrid';
import {type ListingPaginationProps} from './ListingPagination';

export interface ListingPageFilter {
  items: ListingFilterItem[];
  activeId?: string;
  onSelect: (id?: string) => void;
  allLabel: string;
}

export interface ListingPageEmptyState {
  titleKey?: string;
  bodyKey?: string;
  icon: ReactNode;
  illustration?: ImageProps['src'];
  tone?: ListingEmptyStateTone;
}

export interface ListingPageProps {
  title: string;
  eyebrow?: string;
  featured?: ReactNode;
  filter?: ListingPageFilter;
  cards: ListingCardProps[];
  empty: ListingPageEmptyState;
  pagination?: ListingPaginationProps;
  busy?: boolean;
}

// Left-aligned heading block (eyebrow + title) sitting under any hero.
const Heading = styled(Box)(({theme}) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
  marginBottom: theme.spacing(3),
})) as FC<{children?: ReactNode}>;

export function ListingPage({title, eyebrow, featured, filter, cards, empty, pagination, busy}: ListingPageProps) {
  const isEmpty = cards.length === 0;
  return (
    <>
      {featured}
      <Section tone="subtle">
        <Container>
          <Heading>
            {eyebrow && (
              <Typography
                size="body-1"
                weight="medium"
                mobileSize="body-2"
                mobileWeight="medium"
                as="p"
                sx={theme => ({
                  color: theme.palette.colors.text.neutral.default,
                  textTransform: 'capitalize',
                  letterSpacing: 0,
                  [theme.breakpoints.down('md')]: {textTransform: 'capitalize', letterSpacing: 0},
                })}
              >
                {eyebrow}
              </Typography>
            )}
            <Typography
              size="title-3"
              weight="bold"
              mobileSize="title-5"
              mobileWeight="bold"
              as="h1"
              sx={{textTransform: 'capitalize', color: theme => theme.palette.colors.text.neutral.strong}}
            >
              {title}
            </Typography>
          </Heading>
        </Container>
        <ListingGrid
          busy={busy}
          filter={
            filter && (
              <ListingFilterBar items={filter.items} activeId={filter.activeId} onSelect={filter.onSelect} allLabel={filter.allLabel} />
            )
          }
          empty={isEmpty ? empty : undefined}
          pagination={pagination}
        >
          {cards.map((card, index) => (
            <ListingCard key={card.id ?? `${card.title}-${index}`} {...card} />
          ))}
        </ListingGrid>
      </Section>
    </>
  );
}

ListingPage.displayName = 'ListingPage';
